import {
  consentDocumentBlocks,
  type PublicConsentDocument,
  type PublicConsentRequirement,
} from "@emr/contracts";
import { type Ref, useId, useRef, useState } from "react";
import { RequiredMarker } from "../RequiredMarker";
import type { ConsentChoices } from "./consentChoices";

/**
 * The agreement lines under a booking or questionnaire review: "I agree to the terms and privacy
 * notice", required, and the optional, pre-ticked messaging line when the service asks for it.
 *
 * The service asks for the messaging line (a non-null electronicMessaging) only while the clinic
 * offers messages and nothing in scope has answered it yet: never once the booking, or the chart
 * it is linked to, holds a decision, including one staff recorded ("Patient said no", PA-8 K1).
 * So the line is shown ticked or not at all, and a box the patient never touched cannot undo an
 * earlier refusal. The page never shows it from memory: only from the documents passed in.
 *
 * The box and its label toggle the agreement; the link-styled words beside the label open the
 * text. They are a separate button, never inside the label, so the two cannot be confused. On a
 * standalone page the text opens in a modal dialog that returns focus to the button; inside a
 * clinic website's frame a centred dialog could sit outside the visible part of the frame, so the
 * text expands in place instead. Nothing here touches the address or history.
 */
export function ConsentAgreement({
  documents,
  choices,
  onChange,
  embedded = false,
}: {
  readonly documents: PublicConsentRequirement;
  readonly choices: ConsentChoices;
  readonly onChange: (choices: ConsentChoices) => void;
  readonly embedded?: boolean;
}) {
  const { privacyNotice, electronicMessaging } = documents;
  if (privacyNotice === null && electronicMessaging === null) return null;
  return (
    <div className="consent-agreement">
      {privacyNotice === null ? null : (
        <ConsentLine
          document={privacyNotice}
          lead="I agree to the"
          linkText="terms and privacy notice"
          required
          checked={choices.termsAcceptedVersion === privacyNotice.version}
          onCheckedChange={(checked) =>
            onChange({ ...choices, termsAcceptedVersion: checked ? privacyNotice.version : null })
          }
          embedded={embedded}
        />
      )}
      {electronicMessaging === null ? null : (
        <ConsentLine
          document={electronicMessaging}
          lead="I agree to receive"
          linkText="email and text messages"
          required={false}
          checked={choices.messagingAccepted}
          onCheckedChange={(checked) => onChange({ ...choices, messagingAccepted: checked })}
          embedded={embedded}
        />
      )}
    </div>
  );
}

function ConsentLine({
  document,
  lead,
  linkText,
  required,
  checked,
  onCheckedChange,
  embedded,
}: {
  readonly document: PublicConsentDocument;
  /** The label's words before the link-styled button. */
  readonly lead: string;
  readonly linkText: string;
  readonly required: boolean;
  readonly checked: boolean;
  readonly onCheckedChange: (checked: boolean) => void;
  readonly embedded: boolean;
}) {
  const id = `consent-${useId().replaceAll(":", "")}`;
  const [expanded, setExpanded] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const openerRef = useRef<HTMLButtonElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  const showText = () => {
    if (embedded) {
      setExpanded((current) => !current);
      return;
    }
    const dialog = dialogRef.current;
    if (dialog === null || dialog.open) return;
    dialog.showModal();
    // Start at the top of the text, not at the Close button after it.
    titleRef.current?.focus();
  };

  return (
    <div className="consent-agreement-item">
      <div className="consent-line">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(event) => onCheckedChange(event.target.checked)}
          aria-labelledby={`${id}-label ${id}-open`}
          {...(required ? { "aria-required": true } : {})}
        />
        <p>
          <label id={`${id}-label`} htmlFor={id}>
            {lead}
          </label>{" "}
          <button
            id={`${id}-open`}
            ref={openerRef}
            type="button"
            className="consent-text-button"
            onClick={showText}
            {...(embedded
              ? { "aria-expanded": expanded, "aria-controls": `${id}-text` }
              : { "aria-haspopup": "dialog" })}
          >
            {linkText}
          </button>
          {required ? (
            // The visible "Required" belongs to the box like any field's; the name comes from
            // aria-labelledby, so this second label is hidden from assistive technology.
            <label htmlFor={id} className="consent-required" aria-hidden="true">
              <RequiredMarker />
            </label>
          ) : null}
        </p>
      </div>
      {embedded ? (
        <div id={`${id}-text`} className="consent-inline-text" hidden={!expanded}>
          <ConsentDocumentText document={document} titleId={`${id}-title`} />
        </div>
      ) : (
        <dialog
          ref={dialogRef}
          className="consent-dialog"
          aria-labelledby={`${id}-title`}
          onClose={() => openerRef.current?.focus()}
        >
          <ConsentDocumentText document={document} titleId={`${id}-title`} titleRef={titleRef} />
          <div className="consent-dialog-actions">
            <button
              type="button"
              className="continue-button"
              onClick={() => dialogRef.current?.close()}
            >
              Close
            </button>
          </div>
        </dialog>
      )}
    </div>
  );
}

/** A document's title and body, as text: markup in the body shows as typed. */
function ConsentDocumentText({
  document,
  titleId,
  titleRef,
}: {
  readonly document: PublicConsentDocument;
  readonly titleId: string;
  readonly titleRef?: Ref<HTMLHeadingElement>;
}) {
  return (
    <div className="consent-document-text">
      <h2 id={titleId} ref={titleRef} tabIndex={-1}>
        {document.title}
      </h2>
      {consentDocumentBlocks(document.body).map((block, index) => {
        const key = `${index}:${block.kind}`;
        if (block.kind === "heading") return <h3 key={key}>{block.text}</h3>;
        if (block.kind === "paragraph") return <p key={key}>{block.text}</p>;
        return (
          <ul key={key}>
            {block.items.map((item, itemIndex) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: list items are an immutable split of one published version.
              <li key={itemIndex}>{item}</li>
            ))}
          </ul>
        );
      })}
    </div>
  );
}
